import { CoinIcon, CopyrightFooter, Icon, LittleBankersHeaderMark, OinkIcon, Piggington, VaultIcon } from "./Common.jsx";
import { LetterKeyboard, OinkRewardRow, ProgressDots, playDing, playError } from "./Tests.jsx";
import { t as ct } from "../lib/content.js";
import { accentsFor } from "../lib/accents.js";
import { HW_KNOWN_DAYS, historyRow, markItem, newHomeworkList, pickGo } from "../lib/homework.js";
import { fetchSharedList, maskEmail, readGuestResult, rememberSendEmail, rememberedSendEmail, saveGuestResult, sendSharedToApp } from "../lib/homework-share.js";
import { isSpeechAvailable, resetRetryBudget, speak as speakText, stopSpeaking, unlockAudio } from "../lib/speech.js";
import { CHARACTER_CATALOG } from "../lib/oinks.js";
import { todayStr } from "../lib/utils.js";

const { useEffect, useRef, useState } = React;

// LB-382 (Alan, 1 Oct, mock signed off the same day). A shared practice list, opened in a
// browser that has no household on it.
//
// WHY THIS SCREEN EXISTS. A link tapped in WhatsApp or an email on an iPhone opens the browser,
// never the installed app — no manifest setting changes that — and the browser's storage is not
// the app's, so the parent arrives at the marketing page, signed out, with the list nowhere to
// be seen. He called it "incorrect", and it is. What does open the installed app is a push. So
// this page is the fork (his word): a parent who already uses Little Bankers sends the list to
// their app with their email; one who does not is shown the way in.
//
// SENDING IT IS NOT THE END OF THE JOB, and every screen here says so. He considered adding the
// list straight to the child whose school year matches and dropped it himself ("Assumptions are
// always risky"): the parent still opens the app, chooses which child it is for and sets any
// rewards. So the first screen says "You finish in the app", and the last is headed "One step
// left" rather than "Sent".
//
// WHAT THE PAGE CAN KNOW: nothing about whether the address has a household. The worker answers
// the same either way, so the last screen says "if that address has a Little Bankers account"
// and covers both outcomes rather than pretending to know.
//
// The email typed here is kept in this browser only, so the next list is one tap.
// Props are defaulted because the screen is registered by component in cms-screens.mjs, which
// renders it with nothing passed (same rule as HomeworkReceive).
const PREVIEW_ROWS = 3;
// The characters shown on the results page as what Oinks are FOR. One from each price band
// (Alan, 1 Oct: "Show 4 characters, draw each from a price band"), cheapest first, so a child
// sees something close and something to dream about. Names and prices are read from the shop's
// own catalogue; if a preferred character ever leaves it, the first of that band stands in.
const HOOK_PICKS = ["ch-robot", "ch-penguin", "ch-knight", "ch-dragon"];
const HOOK_CHARACTERS = (() => {
  const paid = CHARACTER_CATALOG.filter((c) => !c.starter && c.price > 0);
  const tiers = [...new Set(paid.map((c) => c.tier))];
  return tiers.map((tier) => paid.find((c) => c.tier === tier && HOOK_PICKS.includes(c.id)) || paid.find((c) => c.tier === tier)).slice(0, 4);
})();

// ONE PRACTICE IN THE BROWSER (Alan, 1 Oct: "Non-signed-up and free users can do one practice...
// They can practice once and see results but can't restart or anything"). The third arm of the
// fork, and the recruitment one: a family without the app sees what it does with their own
// child's actual homework before being asked for anything.
//
// It asks the questions exactly as the app does — the same draw (pickGo), the same marking
// (markItem: the slash, bracket and comma rules), the same letter keyboard with the same
// accents, the same wording — because the pure parts live in src/lib/homework.js and the
// keyboard in Tests.jsx, and both are used here as they are. What it is NOT is the app's
// HomeworkRound: that component is kid-mode (reuse-verbatim), reports into a household, pays
// Oinks, offers "Go again" and runs in the full-screen test overlay, which registers itself on
// the app's back stack — none of which exists on a page with no household. So the round is
// drawn here, small, and nothing is saved anywhere except the result of this one go.
//
// A spelling word is read by the device's own voice; the generated clips belong to households.
function GuestRound({ list, onDone, onQuit }) {
  const [items] = useState(() => pickGo(list, todayStr()));
  const [idx, setIdx] = useState(0);
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [slots, setSlots] = useState(() => items.map(() => "pending"));
  const [speechOk, setSpeechOk] = useState(() => isSpeechAvailable());
  const answersRef = useRef([]);
  const advanceRef = useRef(null);
  const item = items[idx];

  useEffect(() => () => { if (advanceRef.current) clearTimeout(advanceRef.current); stopSpeaking(); }, []);

  function say(isAutoplay) {
    if (!item || item.mode !== "spell") return;
    if (!speechOk) { setSpeechOk(true); resetRetryBudget(); }
    speakText(item.answer, { rate: 0.85, isAutoplay, after: item.hint || "", onUnavailable: () => setSpeechOk(false) }); // then its "as in", after a pause (5 Oct)
  }
  // A spelling word is read out a second after it appears, once per word.
  useEffect(() => {
    if (!item || item.mode !== "spell" || !speechOk) return undefined;
    const t = setTimeout(() => say(true), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  function summary() {
    const rows = answersRef.current;
    return { right: rows.filter((r) => r.correct).length, asked: rows.length, rights: rows.filter((r) => r.correct).map((r) => r.row.answer), misses: rows.filter((r) => !r.correct).map((r) => ({ q: r.row.q, answer: r.row.answer, given: r.row.given || "" })) };
  }
  function answer(given, skipped = false) {
    if (result !== null || !item) return;
    const correct = !skipped && markItem(item, given);
    setSlots((prev) => { const next = [...prev]; next[idx] = correct ? "correct" : "wrong"; return next; });
    setResult(correct ? "correct" : "wrong");
    answersRef.current = [...answersRef.current, { correct, row: historyRow(item, skipped ? "" : given, correct) }];
    if (correct) { playDing(); advanceRef.current = setTimeout(() => next(), 700); } else playError();
  }
  function next() {
    setInput(""); setResult(null);
    if (idx + 1 < items.length) { setIdx((i) => i + 1); return; }
    stopSpeaking();
    onDone(summary());
  }
  // Leaving part way: what was answered IS the one practice. With nothing answered, nothing
  // has been used and the visitor goes back to the fork.
  function quit() {
    stopSpeaking();
    if (answersRef.current.length) onDone(summary()); else onQuit();
  }

  if (!item) return null;
  return (
    <div key={`gr-${idx}`} className="bg-white rounded-lg border border-slate-200 px-3 py-5 text-center" data-testid="hwfork-question">
      <div className="flex flex-col mb-3">
        <ProgressDots slots={slots} />
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-slate-500 font-medium text-left">{ct("homework.kid.position", "Word {i} of {n} this go", { i: idx + 1, n: items.length })}</div>
          <button className="text-xs text-slate-500 font-semibold flex-shrink-0 ml-2 min-h-[32px]" onClick={quit} data-testid="hwfork-quit">{ct("tests.quit", "Quit")}</button>
        </div>
      </div>
      {item.mode === "spell" ? (
        <>
          {item.hint && result === null && <p className="text-center text-sm text-slate-500 mb-3 px-4" data-testid="hw-hint">{item.hint}</p>}
          {speechOk ? (
            <button onClick={() => say(false)} className="mx-auto flex items-center gap-2 bg-amber-50 text-amber-700 font-bold rounded-lg px-5 py-3 mb-4" data-testid="hwfork-listen">{ct("tests.listen", "Listen")}</button>
          ) : (
            <button onClick={() => say(false)} className="w-full text-left text-amber-600 bg-amber-50 rounded-xl px-4 py-3 mb-4 text-base font-semibold">{ct("spelling.audio_unavailable", "Audio isn't available right now — tap to try again, or ask a parent to read the word aloud.")}</button>
          )}
          <LetterKeyboard value={input} disabled={result !== null} onEnter={() => answer(input)} maxLength={24} onChange={setInput} />
        </>
      ) : item.mode === "type" ? (
        <>
          <div className="text-xs font-bold text-slate-500 mb-1">{item.dir === "lang2en"
            ? ct("homework.kid.ask_english", "What is the English for")
            : ct("homework.kid.ask_language", "What is the {lang} for", { lang: list.lang || ct("homework.kid.other_language", "other language") })}</div>
          <div className="text-2xl font-extrabold text-slate-900 mb-4" data-testid="hwfork-prompt">{item.prompt}</div>
          <LetterKeyboard value={input} disabled={result !== null} allowSpace placeholder={ct("homework.kid.type_placeholder", "Type your answer")} onEnter={() => answer(input)} maxLength={40} onChange={setInput}
            accents={list.type === "language" && item.dir !== "lang2en" ? accentsFor(list.lang) : null} />
        </>
      ) : (
        <>
          <div className="text-xs font-bold text-slate-500 mb-1">{ct("homework.kid.ask_meaning", "What does this mean")}</div>
          <div className="text-2xl font-extrabold text-slate-900 mb-4" data-testid="hwfork-prompt">{item.prompt}</div>
          <div className="flex flex-col gap-2 mb-2">
            {(item.options || []).map((opt) => (
              <button key={opt} disabled={result !== null} onClick={() => answer(opt)} className={`w-full text-left rounded-xl px-4 py-3 text-sm font-semibold border ${result !== null && opt === item.answer ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-900"}`}>{opt}</button>
            ))}
          </div>
        </>
      )}
      {item.mode !== "choose" && result === null && (
        <>
          <button disabled={!input.trim()} className="bg-slate-900 text-white font-bold rounded-lg px-5 py-2.5 text-base w-full mt-6 disabled:opacity-40" onClick={() => answer(input)} data-testid="hwfork-check">{ct("tests.check_button", "Check")}</button>
          <button className="w-full mt-2 text-sm font-bold text-slate-500 py-2" data-testid="hwfork-skip" onClick={() => answer("", true)}>{ct("tests.skip", "Skip this word")}</button>
        </>
      )}
      {result !== null && (
        <div className={`font-bold text-base mt-3 flex items-center justify-center gap-1.5 ${result === "correct" ? "text-emerald-600" : "text-rose-600"}`} data-testid="hwfork-result">
          <Icon id={result === "correct" ? "ic-celebrate" : "ic-close"} size={18} hue={result === "correct" ? "pink" : "rose"} />
          {result === "correct" ? ct("homework.kid.correct", "Correct") : ct("homework.kid.wrong", "Not quite, it was {answer}", { answer: item.answer })}
        </div>
      )}
      {result === "wrong" && (
        <button className="bg-slate-900 text-white font-bold rounded-lg px-4 py-2.5 text-sm w-full mt-3" onClick={next} data-testid="hwfork-continue">{ct("spelling.continue_button", "Continue")}</button>
      )}
      <p className="text-[11px] text-slate-500 mt-4">{ct("hwfork.try_note", "One practice here in the browser. Nothing is saved.")}</p>
    </div>
  );
}

export function HomeworkFork({ listCode = "", initialList = undefined, onNoApp = () => {}, onGetApp = null }) {
  // undefined = still fetching, null = no longer shared, object = the snapshot.
  // With no code at all (Content Studio renders a registered screen with nothing passed) the
  // fork is drawn around an empty list, so its strings are on screen to be edited.
  const [list, setList] = useState(initialList !== undefined ? initialList : (listCode ? undefined : { name: "", pairs: [], schoolYear: "" }));
  const [step, setStep] = useState("fork"); // fork | email | sent | try | results | full
  // The result of this browser's one practice on this list, if it has had it.
  const [tried, setTried] = useState(() => (listCode ? readGuestResult(listCode) : null));
  const [email, setEmail] = useState(() => rememberedSendEmail());
  const [busy, setBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false); // the sent screen's "Nothing arrived?" (Alan, 2 Oct)
  const [error, setError] = useState("");
  const remembered = rememberedSendEmail();

  useEffect(() => {
    if (initialList !== undefined || !listCode) return;
    let live = true;
    fetchSharedList(listCode).then((l) => { if (live) setList(l || null); });
    return () => { live = false; };
  }, [listCode, initialList]);

  async function send(address) {
    const to = String(address || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) { setError(ct("hwfork.error_email", "Enter the email address you use for Little Bankers.")); return; }
    setError(""); setBusy(true);
    const res = await sendSharedToApp(listCode, to);
    setBusy(false);
    if (res === "limited") { setError(ct("hwfork.error_limited", "That's a lot of tries. Have another go in an hour.")); return; }
    if (res !== "ok") { setError(ct("hwfork.error_send", "That didn't go through. Check your connection and try again.")); return; }
    rememberSendEmail(to);
    setEmail(to);
    setStep("sent");
  }

  // "Get Little Bankers". The list lets a new family past the invite gate (Alan: "let them
  // past the gate"): Root exchanges it for the same creation pass an invite code buys and shows
  // the ordinary set-up, with the list carried across to wait in Requests. When the list has
  // let in as many families as it may, the visitor is shown the waitlist instead. With no
  // onGetApp (Content Studio) it is the old behaviour: on to the landing page.
  async function getApp() {
    if (!onGetApp) { onNoApp(); return; }
    setError(""); setBusy(true);
    const res = await onGetApp(listCode);
    setBusy(false);
    if (res === "ok") return;
    if (res === "full") { setStep("full"); return; }
    setError(res === "limited" ? ct("hwfork.error_limited", "That's a lot of tries. Have another go in an hour.") : ct("hwfork.error_send", "That didn't go through. Check your connection and try again."));
  }

  function startTry() {
    unlockAudio(); // inside the tap, or iOS will not read a spelling word aloud
    setError(""); setStep("try");
  }
  function finishTry(result) {
    saveGuestResult(listCode, result);
    setTried(result);
    setStep("results");
  }

  const pairs = (list && list.pairs) || [];
  const year = String((list && list.schoolYear) || "").trim();
  const meta = [
    ct("hwfork.n_words", "{n} words", { n: pairs.length }),
    year ? ct("hwfork.written_for", "written for {year}", { year }) : "",
    ct("hwfork.from_another", "shared by another family"),
  ].filter(Boolean).join(" · ");

  const shell = (children) => (
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col p-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }} data-testid="hwfork">
      <div className="flex-1 flex items-start justify-center">
        <div className="w-full max-w-sm md:max-w-[560px] space-y-3">
          <div className="flex justify-center pt-2 pb-1"><LittleBankersHeaderMark /></div>
          {children}
        </div>
      </div>
      <CopyrightFooter />
    </div>
  );

  if (list === undefined) {
    return shell(<div className="bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-500" data-testid="hwfork-loading">{ct("hwfork.loading", "Fetching the homework")}</div>);
  }

  if (list === null) {
    return shell(
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hwfork-gone">
        <div className="text-base font-extrabold text-slate-900">{ct("hwfork.gone_title", "This homework is no longer being shared")}</div>
        <p className="text-sm text-slate-500 mt-2">{ct("hwfork.gone_body", "Ask whoever sent it to share it again.")}</p>
        <button onClick={onNoApp} className="w-full mt-4 bg-white border border-slate-200 text-slate-900 text-sm font-bold rounded-lg py-3" data-testid="hwfork-gone-home">{ct("hwfork.gone_home", "Go to Little Bankers")}</button>
      </div>
    );
  }

  const listCard = (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hwfork-list">
      <div className="text-lg font-extrabold text-slate-900 break-words">{list.name || ct("hwfork.a_list", "Homework")}</div>
      <div className="text-xs text-slate-500 mt-1">{meta}</div>
      {step === "fork" && pairs.length > 0 && (
        <div className="mt-3 border-t border-slate-100">
          {pairs.slice(0, PREVIEW_ROWS).map((p, i) => (
            <div key={i} className="py-2 text-sm text-slate-900 border-b border-slate-100 break-words">{p.b ? <>{p.a} <span className="text-slate-400">·</span> <span className="font-bold">{p.b}</span></> : p.a}</div>
          ))}
          {pairs.length > PREVIEW_ROWS && <div className="pt-2 text-xs text-slate-500">{ct("hwfork.and_more", "and {n} more", { n: pairs.length - PREVIEW_ROWS })}</div>}
        </div>
      )}
    </div>
  );

  if (step === "full") {
    return shell(
      <>
        {listCard}
        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hwfork-full">
          <div className="text-base font-extrabold text-slate-900">{ct("hwfork.full_title", "This link has let in as many families as it can")}</div>
          <p className="text-sm text-slate-500 mt-2">{ct("hwfork.full_body", "Little Bankers is invite-only for now. Join the waitlist and we will send you a code.")}</p>
          <button onClick={onNoApp} className="w-full mt-4 bg-slate-900 text-white text-sm font-bold rounded-lg py-3" data-testid="hwfork-full-waitlist">{ct("hwfork.full_cta", "Join the waitlist")}</button>
        </div>
        <button onClick={() => { setError(""); setStep("fork"); }} className="w-full text-slate-900 text-sm font-bold py-2.5 underline underline-offset-2">{ct("hwfork.back", "Back")}</button>
      </>
    );
  }

  if (step === "try") {
    const guestList = newHomeworkList({ code: list.code || listCode || "HW-GUEST1", type: list.type, name: list.name, lang: list.lang, direction: list.direction, pairs, oinksOn: false, until: todayStr() });
    return shell(
      <>
        <div className="text-sm font-extrabold text-slate-900 px-1 break-words" data-testid="hwfork-try-name">{list.name || ct("hwfork.a_list", "Homework")}</div>
        <GuestRound list={guestList} onDone={finishTry} onQuit={() => setStep("fork")} />
      </>
    );
  }

  // THE RESULTS, AND THE HOOK (reworked 1 Oct on Alan's staging pass: "this needs to be a hook
  // but it's pretty plain right now. Can we use a bit of colour (perhaps even handle
  // correct/incorrect answers the way the post-game summary does in app)").
  //
  // So the top card is the app's own end-of-go summary, piece for piece: the score, the pink
  // Oinks row (the same OinkRewardRow), and each miss as a rose row with what was typed struck
  // through beside the right answer — the markup HomeworkRound uses for "To go back for".
  //
  // The Oinks are real. One Oink per right answer is what the app pays for practising a list,
  // and the ones earned here travel with the list: when this family sets up Little Bankers from
  // this page the count is carried on the request, and the child the parent gives the list to
  // is paid them (App.jsx, approveHomeworkSheet). Hence his line, "claim the Oinks you just
  // earned". They are NOT carried by "Send it to my app": that goes through the worker to a
  // household that already exists, and nothing typed in a browser is allowed to credit it.
  //
  // Every button here is only its label; what it does is said in the text above it (his rule,
  // same pass: "each CTA is Action + CTA, not action within the CTA").
  if (step === "results" && tried) {
    const shown = tried.misses.slice(0, 6);
    return shell(
      <>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center" data-testid="hwfork-results">
          <div className="text-xs font-bold text-slate-500 break-words">{list.name || ct("hwfork.a_list", "Homework")}</div>
          <div className="text-4xl font-extrabold text-emerald-600 mt-1 mb-3" data-testid="hwfork-score">{ct("hwfork.results_score", "{right} of {asked} right", { right: tried.right, asked: tried.asked })}</div>
          {tried.right > 0 && <OinkRewardRow op={tried.right} detail={ct("hwfork.results_oinks_detail", "Earned for practising")} />}
          {/* What went RIGHT comes first (Alan, 1 Oct: "Is it demotivating to only show the words
              they got wrong?"). The app's own summary lists only the misses; a child meeting
              Little Bankers for the first time should see their wins before their gaps. */}
          {tried.misses.length > 0 && (tried.rights || []).length > 0 && (
            <div className="text-left mb-4" data-testid="hwfork-rights">
              <div className="text-xs font-bold text-slate-500 mb-1.5">{ct("hwfork.results_rights", "You got these right")}</div>
              <div className="flex flex-wrap gap-1.5">
                {tried.rights.slice(0, 12).map((w, i) => (
                  <span key={i} className="rounded-lg px-2.5 py-1.5 text-xs font-bold bg-emerald-50 text-emerald-700">{w}</span>
                ))}
                {tried.rights.length > 12 && <span className="px-1 py-1.5 text-xs font-semibold text-slate-500">{ct("hwfork.and_more", "and {n} more", { n: tried.rights.length - 12 })}</span>}
              </div>
            </div>
          )}
          {tried.misses.length === 0 ? (
            <p className="text-sm font-bold text-emerald-600">{ct("hwfork.results_all_right", "Every one right today.")}</p>
          ) : (
            <div className="text-left" data-testid="hwfork-misses">
              <div className="text-xs font-bold text-slate-500 mb-1.5">{ct("homework.kid.go_back_for", "To go back for")}</div>
              <div className="flex flex-col gap-1.5">
                {shown.map((m, i) => (
                  <div key={i} className="rounded-lg px-3 py-2 text-xs font-semibold bg-rose-50 text-rose-700 flex items-center justify-between gap-2">
                    <span className="truncate">{m.q === m.answer ? <b>{m.answer}</b> : m.q}</span>
                    <span className="text-rose-500 truncate">{m.given ? <><s className="opacity-70">{m.given}</s> </> : null}{m.q === m.answer ? null : <b>{m.answer}</b>}</span>
                  </div>
                ))}
              </div>
              {tried.misses.length > shown.length && <div className="pt-2 text-xs text-slate-500">{ct("hwfork.and_more", "and {n} more", { n: tried.misses.length - shown.length })}</div>}
            </div>
          )}
        </div>
        {/* The hook. Only the part ABOUT Oinks is in the Oinks' own pink (Alan, 1 Oct: "Yellow
            tint is wrong for Oinks", then "I don't think the entire bottom section needs to be
            pink"); the rest is an ordinary white card.
            A child who has never opened the app does not know what an Oink is, so it is SHOWN:
            the characters Oinks unlock, drawn by the app's own Piggington from its own
            catalogue, with their real prices ("pictures > words"). */}
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="hwfork-hook">
          <div className="text-base font-extrabold text-slate-900">{tried.right > 0
            ? ct("hwfork.hook_title_oinks", "Want to carry on practising and claim the {n} Oinks you just earned?", { n: tried.right })
            : ct("hwfork.hook_title", "Want to carry on practising?")}</div>
          <div className="mt-3 rounded-lg bg-pink-50 border border-pink-200 p-3" data-testid="hwfork-oinks-explained">
            <div className="flex items-center gap-2">
              <OinkIcon className="w-6 h-6 flex-shrink-0" />
              <div className="text-sm font-bold text-slate-900">{ct("hwfork.oinks_what", "Oinks are what you earn for practising, and you save them up for new characters.")}</div>
            </div>
            <div className="mt-3 flex justify-between gap-2">
              {HOOK_CHARACTERS.map((c) => (
                <div key={c.id} className="flex-1 min-w-0 text-center" data-testid="hwfork-character">
                  <Piggington base={c.id} className="h-16 w-auto mx-auto" />
                  <div className="text-xs font-bold text-slate-900 mt-1">{c.label}</div>
                  <div className="text-[11px] font-semibold text-pink-600">{ct("hwfork.oinks_price", "{n} Oinks", { n: c.price })}</div>
                </div>
              ))}
            </div>
          </div>
          {/* "Can we highlight some other things the kid/parent will like?" Three for the child,
              one for the grown-up, each a thing the app really does and a whole sentence. */}
          <ul className="mt-3 space-y-2.5" data-testid="hwfork-highlights">
            <li className="flex items-start gap-2.5 text-sm text-slate-700"><Icon id="ic-brain" size={20} hue="pink" className="flex-shrink-0 mt-0.5" /><span>{ct("hwfork.like_games", "Play practice games for spellings, times tables and maths.")}</span></li>
            <li className="flex items-start gap-2.5 text-sm text-slate-700"><CoinIcon className="w-5 h-5 flex-shrink-0 mt-0.5" /><span>{ct("hwfork.like_tokens", "Earn tokens for helping at home, then save them or spend them on rewards.")}</span></li>
            <li className="flex items-start gap-2.5 text-sm text-slate-700"><VaultIcon className="w-5 h-5 flex-shrink-0 mt-0.5" /><span>{ct("hwfork.like_vault", "Save up in a Savings Vault that can pay interest every week.")}</span></li>
            <li className="flex items-start gap-2.5 text-sm text-slate-700"><Icon id="ic-gift" size={20} hue="pink" className="flex-shrink-0 mt-0.5" /><span>{ct("hwfork.like_homework", "Get rewarded for doing your homework.")}</span></li>
          </ul>
          {/* Alan, 1 Oct: "I'm confused what the Get started CTA does?" It opens sign-up, so
              the text says so, says what will be waiting afterwards, and the button is named
              for exactly that. */}
          <p className="text-sm font-semibold text-slate-900 mt-4">{tried.right > 0
            ? ct("hwfork.hook_body_oinks", "Ask your grown-up to create an account. Once they have set it up, this homework and your {n} Oinks will be waiting in the app.", { n: tried.right })
            : ct("hwfork.hook_body", "Ask your grown-up to create an account. Once they have set it up, this homework will be waiting in the app.")}</p>
          {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mt-3" data-testid="hwfork-error">{error}</div>}
          <button disabled={busy} onClick={getApp} className="w-full mt-3 bg-slate-900 text-white text-sm font-bold rounded-lg py-3 text-center disabled:opacity-40" data-testid="hwfork-hook-get">{ct("hwfork.hook_cta", "Create an account")}</button>
        </div>
        <p className="text-xs text-slate-500 text-center">
          {ct("hwfork.hook_have_app", "Already have the app?")}{" "}
          <button onClick={() => { setError(""); if (remembered) send(remembered); else setStep("email"); }} className="font-bold text-slate-900 underline underline-offset-2" data-testid="hwfork-hook-send">{ct("hwfork.send", "Send it to my app")}</button>
        </p>
      </>
    );
  }

  if (step === "sent") {
    const steps = [
      ct("hwfork.sent_step_1", "Tap the notification"),
      ct("hwfork.sent_step_2", "Choose whose homework it is"),
      ct("hwfork.sent_step_3", "Check the words and set any rewards"),
    ];
    return shell(
      <>
        {listCard}
        {/* Alan, 1 Oct: "It's quite plain. Is there any opportunity to break it up a little bit
            with some colour?" The part that went well is green, with a tick and green step
            numbers; the "Nothing arrived?" card under it stays plain, so the two read apart. */}
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4" data-testid="hwfork-sent">
          <div className="flex items-center gap-2 text-base font-extrabold text-emerald-800"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center"><Icon id="ic-check" size={14} fill="#ffffff" /></span>{ct("hwfork.sent_title", "Nearly there")}</div>
          <p className="text-sm text-slate-700 mt-2">{ct("hwfork.sent_body", "We've sent a notification to the Little Bankers app linked to {email}. All that's left is to:", { email })}</p>
          <ol className="mt-3 space-y-2">
            {steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm font-semibold text-slate-900">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-extrabold flex items-center justify-center">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
        {/* Alan, 2 Oct: "Nothing arrived?" is a header that opens; the two cases sit behind it,
            and "Try a different email" belongs to the second, so it lives inside that case
            rather than under the card. Closed by default: the green card above is the answer
            most people need. */}
        <div className="bg-white rounded-lg border border-slate-200" data-testid="hwfork-help">
          <button type="button" aria-expanded={helpOpen} onClick={() => setHelpOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 p-4 text-left" data-testid="hwfork-help-toggle">
            <span className="text-base font-extrabold text-slate-900">{ct("hwfork.sent_help_title", "Nothing arrived?")}</span>
            <Icon id={helpOpen ? "ic-chevron-up" : "ic-chevron-down"} size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />
          </button>
          {helpOpen && (
            <div className="px-4 pb-4 space-y-3" data-testid="hwfork-help-body">
              <div>
                <div className="text-sm font-bold text-slate-900">{ct("hwfork.sent_no_push_title", "Notifications are off")}</div>
                <p className="text-xs text-slate-500 mt-0.5">{ct("hwfork.sent_no_push_body", "The homework is still waiting for you in Requests when you open the app, and it's worth switching notifications on so you don't miss the next one.")}</p>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="text-sm font-bold text-slate-900">{ct("hwfork.sent_no_account_title", "Nothing in Requests either")}</div>
                <p className="text-xs text-slate-500 mt-0.5">{ct("hwfork.sent_no_account_body", "That email isn't linked to your app, so try again with the one you signed up with.")}</p>
                <button disabled={busy} onClick={() => { setError(""); setHelpOpen(false); setStep("email"); }} className="w-full mt-3 bg-white border border-slate-200 text-slate-900 text-sm font-bold rounded-lg py-3" data-testid="hwfork-other-email">{ct("hwfork.sent_other_email", "Try a different email")}</button>
              </div>
            </div>
          )}
        </div>
        {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2" data-testid="hwfork-error">{error}</div>}
        <p className="text-xs text-slate-500 text-center">
          {ct("hwfork.new_heading", "New to Little Bankers?")}{" "}
          <button disabled={busy} onClick={getApp} className="font-bold text-slate-900 underline underline-offset-2" data-testid="hwfork-sent-no-app">{ct("hwfork.get", "Get Little Bankers")}</button>
        </p>
      </>
    );
  }

  if (step === "email") {
    return shell(
      <>
        {listCard}
        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hwfork-email">
          <div className="text-base font-extrabold text-slate-900">{ct("hwfork.email_title", "Already got the app installed?")}</div>
          <p className="text-sm text-slate-500 mt-2">{ct("hwfork.email_body", "Enter an email address linked to your Little Bankers app and we'll send this homework to it.")}</p>
          <label htmlFor="hwfork-email-input" className="block text-xs font-bold text-slate-900 mt-4 mb-1">{ct("hwfork.email_label", "Email")}</label>
          <input
            id="hwfork-email-input" type="email" inputMode="email" autoComplete="email" autoCapitalize="none"
            className="border border-slate-200 rounded-lg px-3 py-3 text-sm w-full"
            placeholder={ct("hwfork.email_placeholder", "you@example.com")}
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(email); }}
            data-testid="hwfork-email-input"
          />
          {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mt-3" data-testid="hwfork-error">{error}</div>}
          <button disabled={busy} onClick={() => send(email)} className="w-full mt-4 bg-slate-900 text-white text-sm font-bold rounded-lg py-3 disabled:opacity-40" data-testid="hwfork-email-send">{busy ? ct("hwfork.sending", "Sending") : ct("hwfork.email_send", "Send it to my app")}</button>
          {/* Alan, 1 Oct: "Highlight push and/or requests and the steps they need to take
              afterwards." What happens once Send is pressed, before it is pressed. */}
          <div className="mt-4 rounded-lg bg-slate-50 px-3 py-3" data-testid="hwfork-email-next">
            <div className="text-sm font-bold text-slate-900">{ct("hwfork.email_next_title", "What happens next")}</div>
            <p className="text-sm text-slate-500 mt-1">{ct("hwfork.email_next_body", "You'll get a notification on your phone, and the homework will also be waiting in Requests when you open the app. From there you only need to:")}</p>
            <ol className="mt-2 space-y-2">
              {[ct("hwfork.sent_step_2", "Choose whose homework it is"), ct("hwfork.sent_step_3", "Check the words and set any rewards")].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm font-semibold text-slate-900">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-pill text-navy text-xs font-extrabold flex items-center justify-center">{i + 1}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </div>
          <p className="text-xs text-slate-500 mt-3">{ct("hwfork.email_note", "We'll remember this email on this phone, so next time it only takes one tap.")}</p>
        </div>
        <button onClick={() => { setError(""); setStep("fork"); }} className="w-full text-slate-900 text-sm font-bold py-2.5 underline underline-offset-2" data-testid="hwfork-email-back">{ct("hwfork.back", "Back")}</button>
      </>
    );
  }

  // The fork (reworked 1 Oct on Alan's staging pass). Two questions, each with its own heading
  // and each ending in something that LOOKS like a button: "the send section... doesn't look
  // like an action and a CTA", and "Try it now and Get Little Bankers don't look like CTAs.
  // Make it so and make them look more appealing".
  //   Got the app installed?   -> Send it to my app          (filled)
  //   New to Little Bankers?   -> Try it now                 (filled: "the higher priority one...
  //                                                           because it will offer value pre-install")
  //                            -> Get Little Bankers         (outlined, second to Try)
  // So this screen has two filled buttons, one per question, on purpose: a visitor is one kind
  // of family or the other and each kind has one obvious thing to press.
  const cta = "w-full text-sm font-bold rounded-lg py-3 text-center disabled:opacity-40";
  return shell(
    <>
      {listCard}
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hwfork-choices">
        <div className="text-base font-extrabold text-slate-900">{ct("hwfork.send_heading", "Got the app installed?")}</div>
        <p className="text-sm text-slate-500 mt-1">{ct("hwfork.send_body", "Send this homework straight to it. Once you're in the app, pick whose it is and set any rewards.")}</p>
        <button
          disabled={busy}
          onClick={() => { if (remembered) send(remembered); else { setError(""); setStep("email"); } }}
          className={`${cta} mt-3 bg-slate-900 text-white`}
          data-testid="hwfork-send"
        >{busy ? ct("hwfork.sending", "Sending") : ct("hwfork.send", "Send it to my app")}</button>
        {remembered && (
          <p className="text-xs text-slate-500 mt-2" data-testid="hwfork-send-to">
            {ct("hwfork.send_to", "We'll send it to {email}, or you can", { email: maskEmail(remembered) })}{" "}
            <button onClick={() => { setError(""); setEmail(""); setStep("email"); }} className="font-bold text-slate-900 underline underline-offset-2" data-testid="hwfork-not-you">{ct("hwfork.not_you", "use a different email")}</button>.
          </p>
        )}
        {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mt-3" data-testid="hwfork-error">{error}</div>}
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hwfork-new">
        <div className="text-base font-extrabold text-slate-900">{ct("hwfork.new_heading", "New to Little Bankers?")}</div>
        {pairs.length > 0 && (
          <div className="mt-1 pb-4 border-b border-slate-100">
            <p className="text-sm text-slate-500">{tried ? ct("hwfork.try_done_sub", "You've had your free practice. Here's how it went.") : ct("hwfork.try_body", "See how your child gets on with these words. One free practice, right here, with no account needed.")}</p>
            <button onClick={() => { if (tried) { setError(""); setStep("results"); } else startTry(); }} className={`${cta} mt-3 bg-slate-900 text-white`} data-testid="hwfork-try">{tried ? ct("hwfork.try_done", "See your result") : ct("hwfork.try", "Try it now")}</button>
          </div>
        )}
        <p className={`text-sm text-slate-500 ${pairs.length > 0 ? "mt-4" : "mt-1"}`}>{ct("hwfork.get_body", "Set up Little Bankers and this homework will be waiting for you in the app.")}</p>
        <button disabled={busy} onClick={getApp} className={`${cta} mt-3 bg-white text-slate-900 border-2 border-slate-900`} data-testid="hwfork-no-app">{ct("hwfork.get", "Get Little Bankers")}</button>
      </div>
    </>
  );
}
